/**
 * GiftGo — Bottom Navigation Bar (SPA)
 * 
 * Renders the floating bottom nav and emits route changes via callback.
 * Mirrors the Jinja template partials/navigation.html but as a JS component.
 */

import { TASKS_COUNT_EVENT } from '../../utils/tasksCount.js';

const NAV_ITEMS = [
  { path: '/leaderboard', icon: 'nav-leaderboard', i18nKey: 'common.tournaments', label: 'Tournaments' },
  { path: '/events',      icon: 'nav-events',      i18nKey: 'common.giveaways',   label: 'Giveaways' },
  { path: '/play',        icon: 'nav-games',        i18nKey: 'common.play',        label: 'Play' },
  { path: '/tasks',       icon: 'nav-tasks',        i18nKey: 'common.tasks',       label: 'Tasks' },
  { path: '/profile',     icon: 'nav-profile',      i18nKey: 'common.profile',     label: 'Profile' },
];

class BottomNav {
  /**
   * @param {Function} onNavigate — (path: string) => void
   */
  constructor(onNavigate) {
    this.onNavigate = onNavigate;
    this.element = null;
    this._handlers = [];
  }

  /**
   * Ставит аватарку пользователя вместо иконки профиля.
   * Фото берём как и раньше: photo_url из Telegram, иначе прокси /api/user_photo,
   * иначе первая буква имени.
   * @param {Object} user — данные пользователя (photo_url, id, first_name)
   */
  updateAvatar(user) {
    const slot = this.element?.querySelector('.nav-item[data-path="/profile"] .nav-icon');
    if (!slot || !user) return;

    const letter = (user.first_name || '?').charAt(0).toUpperCase();
    const photo = user.photo_url || (user.id ? `/api/user_photo/${user.id}` : '');
    slot.classList.add('nav-icon--avatar');

    if (!photo) {
      slot.innerHTML = '';
      const stub = document.createElement('span');
      stub.className = 'nav-avatar-letter';
      stub.textContent = letter;
      slot.appendChild(stub);
      return;
    }
    if (slot.querySelector('img')?.getAttribute('src') === photo) return;

    const img = document.createElement('img');
    img.className = 'nav-avatar';
    img.alt = '';
    img.addEventListener('error', () => {
      // Фото не отдалось — остаётся буква, а не пустой кружок
      slot.innerHTML = '';
      const stub = document.createElement('span');
      stub.className = 'nav-avatar-letter';
      stub.textContent = letter;
      slot.appendChild(stub);
    });
    img.src = photo;
    slot.innerHTML = '';
    slot.appendChild(img);
  }

  /**
   * Render into a container element (typically appended to body or app shell).
   */
  /**
   * Get translated label for a nav item, with English fallback.
   * @param {Object} item — nav item config
   * @returns {string}
   */
  _getLabel(item) {
    if (window.i18n?.t) {
      const val = window.i18n.t(item.i18nKey);
      if (val && val !== item.i18nKey) return val;
    }
    return item.label;
  }

  render(container) {
    // Listen for ticket balance updates from any page (e.g. PlayPage spins)
    this._ticketHandler = (e) => {
      const count = e.detail?.ticketBalance;
      const joined = e.detail?.joined;
      if (count != null) {
        // If balance is 0 and not joined → show "1" (free ticket available)
        const badge = (count === 0 && joined === false) ? 1 : count;
        this.updateBadge('/events', badge);
      }
    };
    document.addEventListener('ticketBalanceUpdated', this._ticketHandler);

    // Listen for available-tasks count updates (Tasks page fetch/complete, bootstrap)
    this._tasksCountHandler = (e) => {
      const count = e.detail?.count;
      if (count != null) this.updateBadge('/tasks', count);
    };
    document.addEventListener(TASKS_COUNT_EVENT, this._tasksCountHandler);

    const nav = document.createElement('div');
    nav.className = 'bottom-nav';
    nav.innerHTML = NAV_ITEMS.map(item => `
      <div class="nav-item" data-path="${item.path}">
        <div class="nav-icon ${item.icon.replace('nav-', '')}-icon">
          <svg class="nav-icon-svg" aria-hidden="true">
            <use href="#${item.icon}"></use>
          </svg>
        </div>
        <div class="nav-text" data-i18n="${item.i18nKey}">${this._getLabel(item)}</div>
      </div>
    `).join('');

    container.appendChild(nav);
    this.element = nav;

    // Attach click handlers
    nav.querySelectorAll('.nav-item').forEach(el => {
      const handler = () => {
        const path = el.dataset.path;
        this.onNavigate(path);
      };
      el.addEventListener('click', handler);
      this._handlers.push({ el, handler });
    });
  }

  /**
   * Highlight the active nav item.
   */
  setActive(path) {
    if (!this.element) return;
    this.element.querySelectorAll('.nav-item').forEach(el => {
      el.classList.toggle('active', el.dataset.path === path);
    });
  }

  /**
   * Update translations on nav labels.
   * @param {Function} t — translation function
   */
  updateTranslations(t) {
    if (!this.element) return;
    this.element.querySelectorAll('[data-i18n]').forEach(el => {
      const key = el.getAttribute('data-i18n');
      const translated = t(key);
      if (translated && translated !== key) {
        el.textContent = translated;
      }
    });
  }

  /**
   * Show or hide a numeric badge on a nav item.
   * @param {string} path — route path (e.g. '/events')
   * @param {number} count — badge number (0 hides badge)
   */
  updateBadge(path, count) {
    if (!this.element) return;
    const item = this.element.querySelector(`.nav-item[data-path="${path}"]`);
    if (!item) return;
    const icon = item.querySelector('.nav-icon');
    if (!icon) return;
    let badge = icon.querySelector('.nav-badge');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'nav-badge';
        icon.appendChild(badge);
      }
      badge.textContent = count > 99 ? '99+' : count;
    } else if (badge) {
      badge.remove();
    }
  }

  /**
   * Cleanup.
   */
  destroy() {
    this._handlers.forEach(({ el, handler }) => {
      el.removeEventListener('click', handler);
    });
    this._handlers = [];
    if (this._ticketHandler) {
      document.removeEventListener('ticketBalanceUpdated', this._ticketHandler);
      this._ticketHandler = null;
    }
    if (this._tasksCountHandler) {
      document.removeEventListener(TASKS_COUNT_EVENT, this._tasksCountHandler);
      this._tasksCountHandler = null;
    }
    if (this.element) {
      this.element.remove();
      this.element = null;
    }
  }
}

export default BottomNav;
